// Prefer MediaPipe Hands (CDN, CORS-safe); fall back to ml5 handpose
import { Hands } from '@mediapipe/hands';
import { HandData, HandLandmarks } from './types';

class HandTracker {
  private handpose: any;
  private mpHands: Hands | null = null;
  private onHandsDetected: (hands: HandData[]) => void;
  private videoElement: HTMLVideoElement | null = null;
  private initialized: boolean = false;
  private isInitializing: boolean = false;
  private isSending: boolean = false;
  private runtime: 'ml5' | 'mediapipe' | null = null;
  private pinchEma: number | null = null;
  private emaAlpha: number = 0.4; // smoothing for pinch distance
  private lastSendTimeMs: number = 0;
  
  constructor(onHandsDetected: (hands: HandData[]) => void) {
    this.onHandsDetected = onHandsDetected;
    
    // ml5 handpose will be created in initialize
  }
  
  public async initialize(videoElement: HTMLVideoElement) {
    this.videoElement = videoElement;
    if (this.initialized) return;
    this.isInitializing = true;
    // Try MediaPipe first (no TFHub/CORS). If it fails, fall back to ml5 handpose
    try {
      await this.initializeMediaPipe();
      this.runtime = 'mediapipe';
      this.initialized = true;
      console.log('MediaPipe Hands initialized');
    } catch (err) {
      console.warn('MediaPipe Hands failed, falling back to ml5 handpose', err);
      const ml5mod: any = await import('ml5');
      this.handpose = await ml5mod.default.handpose(this.videoElement);
      this.handpose.on('predict', (results: any[]) => {
        const handData: HandData[] = results.map((res: any) => ({
          // ml5 returns pixel coordinates; normalize to [0,1]
          landmarks: res.landmarks.map((p: number[]) => ({
            x: p[0] / this.videoElement!.videoWidth,
            y: p[1] / this.videoElement!.videoHeight,
            z: 0,
          })),
          handedness: res.handedness || 'Unknown',
        }));
        this.onHandsDetected(handData);
      });
      this.runtime = 'ml5';
      this.initialized = true;
      console.log('ml5 handpose initialized');
    } finally {
      this.isInitializing = false;
    }
  }
  
  public startTracking() {
    // no-op: ml5 emits predict events automatically
  }
  
  public async update() {
    // ml5 handpose handles its own prediction loop via 'predict' events
    if (this.runtime === 'mediapipe' && this.mpHands && this.videoElement && !this.isSending) {
      // Throttle inference to ~15 FPS to reduce main-thread load
      const now = performance.now();
      const minIntervalMs = 1000 / 15; // ~66ms
      if (now - this.lastSendTimeMs < minIntervalMs) {
        return;
      }
      this.lastSendTimeMs = now;

      this.isSending = true;
      try {
        await this.mpHands.send({ image: this.videoElement });
      } finally {
        this.isSending = false;
      }
    }
    return;
  }
  
  public destroy() {
    if (this.handpose && this.handpose.dispose) {
      try { this.handpose.dispose(); } catch {}
    }
    if (this.mpHands) {
      try { (this.mpHands as any).close?.(); } catch {}
      this.mpHands = null;
    }
  }
  
  // Calculate 2D distance between thumb tip (4) and index finger tip (8)
  public calculatePinchDistance(hand: HandData): number | null {
    if (hand.landmarks.length < 9) return null;
    const thumbTip = hand.landmarks[4];
    const indexTip = hand.landmarks[8];
    const dx = thumbTip.x - indexTip.x;
    const dy = thumbTip.y - indexTip.y;
    const raw = Math.sqrt(dx * dx + dy * dy);
    // EMA smoothing to reduce flicker
    if (this.pinchEma === null) {
      this.pinchEma = raw;
    } else {
      this.pinchEma = this.emaAlpha * raw + (1 - this.emaAlpha) * this.pinchEma;
    }
    return this.pinchEma;
  }

  // Get position of index finger tip (landmark 8) for cursor positioning
  public getIndexFingerTipPosition(hand: HandData): HandLandmarks | null {
    if (hand.landmarks.length < 9) return null;
    
    return hand.landmarks[8]; // Index finger tip
  }
  // Get position of thumb tip (landmark 4)
  public getThumbTipPosition(hand: HandData): HandLandmarks | null {
    if (hand.landmarks.length < 5) return null;
    return hand.landmarks[4];
  }
  // Get midpoint of pinch (mid of thumb tip and index tip)
  public getPinchMidpoint(hand: HandData): HandLandmarks | null {
    if (hand.landmarks.length < 9) return null;
    const t = hand.landmarks[4];
    const i = hand.landmarks[8];
    return { x: (t.x + i.x) / 2, y: (t.y + i.y) / 2, z: 0 };
  }

  public getWristPosition(hand: HandData): HandLandmarks | null {
    if (hand.landmarks.length < 1) return null;
    return hand.landmarks[0];
  }

  public getLandmarkPosition(hand: HandData, index: number): HandLandmarks | null {
    if (hand.landmarks.length <= index) return null;
    return hand.landmarks[index];
  }

  // Initialize MediaPipe Hands (moved inside class to satisfy TypeScript)
  private async initializeMediaPipe(): Promise<void> {
    this.mpHands = new Hands({
      locateFile: (file: string) => `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`,
    });
    (this.mpHands as Hands).setOptions({
      // Performance-first settings
      maxNumHands: 1, // track only one hand for speed
      modelComplexity: 0, // lite model
      minDetectionConfidence: 0.4,
      minTrackingConfidence: 0.4,
    });
    (this.mpHands as Hands).onResults((results: any) => {
      const landmarksList: any[] = results.multiHandLandmarks || [];
      const handednessList: MediaPipeHandednessItem[] = results.multiHandedness || [];
      const hands: HandData[] = landmarksList.map((lm: any[], idx: number) => ({
        // MediaPipe returns normalized coordinates in [0,1]
        landmarks: lm.map((p: any) => ({ x: p.x, y: p.y, z: p.z ?? 0 })),
        handedness: handednessList[idx]?.label || 'Unknown',
      }));
      this.onHandsDetected(hands);
    });
  }

}

interface MediaPipeHandednessItem {
  label?: string;
}

export default HandTracker;